import { QueryClient, dehydrate, hydrate } from "@tanstack/react-query";
import { CATALOG_DATA_CACHE_TIME } from "shared/config/queryConstants";
import { STANDARD_PRODUCT_CATEGORIES_QUERY_KEY } from "shared/api/hooks/standardProductCategoriesQuery";

const CACHE_VERSION = "standard-product-categories:v1";

const STORAGE_KEY = "rq:standard-product-categories";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      cacheTime: CATALOG_DATA_CACHE_TIME,
    },
  },
});

const restoreCache = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;

    const { version, timestamp, cacheState } = JSON.parse(raw);

    if (
      version !== CACHE_VERSION ||
      Date.now() - timestamp > CATALOG_DATA_CACHE_TIME
    ) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    hydrate(queryClient, cacheState);
  } catch {
    localStorage.removeItem(STORAGE_KEY);
  }
};

/** Persist only the standard-product-categories query to localStorage */
const saveCache = () => {
  try {
    const dehydrated = dehydrate(queryClient, {
      shouldDehydrateQuery: (query) =>
        query.queryKey[0] === STANDARD_PRODUCT_CATEGORIES_QUERY_KEY,
    });
    const entry = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      cacheState: dehydrated,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Quietly ignore localstorage failures
  }
};

restoreCache();

let saveTimer: ReturnType<typeof setTimeout> | null = null;
queryClient.getQueryCache().subscribe((event) => {
  if (event?.query?.queryKey[0] !== STANDARD_PRODUCT_CATEGORIES_QUERY_KEY) {
    return;
  }

  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveCache();
    saveTimer = null;
  }, 1_000);
});
